import { useEffect, useRef, useState } from 'react';

type Dimensions = {
  width: number;
  height: number;
};

export const useContainerDimensions = (
  shouldObserve: boolean,
  defaultDimensions: Dimensions = { width: 0, height: 0 }
) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState<Dimensions>(defaultDimensions);
  const resizeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!containerRef.current || !shouldObserve) return;

    const resizeObserver = new ResizeObserver((entries) => {
      // Debounce resize handling to prevent excessive re-renders
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }

      resizeTimeoutRef.current = setTimeout(() => {
        const { width, height } = entries[0].contentRect;
        // Only update if dimensions actually changed
        setDimensions((prev) => {
          if (prev.width === width && prev.height === height) {
            return prev;
          }
          return { width, height };
        });
      }, 100);
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      resizeObserver.disconnect();
    };
  }, [shouldObserve]);

  return { containerRef, dimensions };
};
